import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { trackEdits, hasUnsavedWork } from './utils/unsavedWork';

// Register the PWA service worker on boot. Previously this only ran
// inside registerPushNotifications(), which is gated behind a VAPID
// key — so the service worker was never registered unless push was
// configured, and the app-shell cache / install-to-home-screen didn't
// work. Registering here makes the PWA installable and enables the
// offline shell; push registration still happens separately when the
// user opts in.
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      if (!reg) return;

      // Ask whether a new version exists. A browser only checks for a new
      // service worker on navigation or roughly daily, and an installed PWA
      // may go days without either -- so a deploy can sit unseen while the
      // app keeps serving the bundle it started with. That is exactly what
      // happened with the Housy AI rename: it was live on the server and
      // still read "Housy" on the phone.
      const poll = () => reg.update().catch(() => {});
      setInterval(poll, 60 * 60 * 1000);
      // And whenever the app comes back to the foreground, which is the
      // moment someone is about to look at it.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") poll();
      });
    }).catch(() => {
      // silent — dev Safari and private windows can block SW
    });
  });

  // sw.js calls skipWaiting() and clients.claim(), so a new worker takes
  // control the moment it installs; the page still runs the old bundle
  // until it reloads. It used to reload at once, discarding whatever was
  // being typed -- a journal entry, a lease draft, a signature (audit
  // OPS-9). Now a page someone is looking at is never reloaded under them:
  // a bar offers the reload. A hidden page reloads only when nothing is in
  // progress on it (an open dialog, a field still on screen that was typed
  // into); otherwise it gets the bar too.
  trackEdits();
  let updateReady = false, hiddenAt = document.visibilityState === "hidden" ? Date.now() : 0;
  const reloadIfIdle = () => {
    if (document.visibilityState === "visible" || hasUnsavedWork()) return false;
    window.location.reload();
    return true;
  };
  const showUpdateBar = () => {
    if (document.getElementById("hx-update-bar")) return;
    const bar = document.createElement("div");
    bar.id = "hx-update-bar";
    bar.setAttribute("role", "status");
    bar.style.cssText = "position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:2147483000;background:#1f2937;color:#fff;padding:10px 14px;border-radius:12px;font:14px system-ui,sans-serif;display:flex;gap:12px;align-items:center;box-shadow:0 8px 24px rgba(0,0,0,.25);max-width:calc(100vw - 32px)";
    const label = document.createElement("span"); label.textContent = "A new version of Housify is ready.";
    const btn = document.createElement("button"); btn.type = "button"; btn.textContent = "Reload";
    btn.style.cssText = "background:#6366f1;color:#fff;border:0;border-radius:8px;padding:6px 12px;font-weight:600;cursor:pointer";
    btn.onclick = () => window.location.reload();
    bar.append(label, btn); document.body.appendChild(bar);
  };
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // On a FIRST install there was no previous controller and nothing is stale.
    if (updateReady || !navigator.serviceWorker.controller) return;
    updateReady = true;
    if (reloadIfIdle()) return;
    if (document.visibilityState === "hidden") return;   // handled when it comes back
    showUpdateBar();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      if (updateReady) reloadIfIdle();
      return;
    }
    if (!updateReady) return;
    // Away 30+ minutes with nothing in progress: reload as it comes back,
    // before any typing starts.
    if (hiddenAt && Date.now() - hiddenAt > 30 * 60 * 1000 && !hasUnsavedWork()) window.location.reload();
    else showUpdateBar();
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
